import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

export default function Lightbox({ project, onClose }) {
  const reduce = useReducedMotion();
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    closeRef.current && closeRef.current.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return createPortal(
    <AnimatePresence>
      {project && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8 bg-ink/85 backdrop-blur-sm"
          initial={reduce ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduce ? {} : { opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`${project.title} before and after`}
          data-testid="lightbox"
        >
          <motion.div
            className="relative w-full max-w-md bg-cream p-4 sm:p-6"
            initial={reduce ? {} : { scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={reduce ? {} : { scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute -top-3 -right-3 z-10 h-10 w-10 rounded-full bg-ink text-white flex items-center justify-center"
              data-testid="lightbox-close"
            >
              <X size={20} />
            </button>
            <BeforeAfterSlider project={project} priority />
            {project.description && (
              <p className="mt-3 text-sm text-ink/70 leading-relaxed">
                {project.description}
              </p>
            )}
            <p className="mt-2 text-xs text-ink/50">
              Drag the handle to compare before &amp; after.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
